import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Clock, MapPin, LogIn, LogOut, Loader2, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  currentMonthIST,
  formatISTTime,
  monthDaysIST,
  monthLabelIST,
  monthRangeIST,
  todayIST,
} from "../../lib/datetime";
import {
  AttendanceCalendar,
  AttendanceMonthSummary,
  StatusPill,
} from "./MyAttendancePage";

// Personal check-in / check-out + calendar card for manager roles, who mark
// and review their own attendance from the team view. Mirrors MyAttendancePage
// but scoped via so_id override (backend: routes_attendance.py:210,252).
//
// "Mirrors" used to mean "holds a second copy of": the calendar, the legend, the
// status colour map and the summary line were duplicated verbatim, so the same
// Pattern A defect existed twice and could be fixed once. They are now imported
// from MyAttendancePage and there is exactly one implementation of each.
export default function SelfCheckInCard() {
  const { user } = useAuth();
  const start = currentMonthIST();
  const [year, setYear] = useState(start.year);
  const [month, setMonth] = useState(start.month);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todayRec, setTodayRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { first, last } = monthRangeIST(year, month);
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/attendance", { params: { so_id: user.id, from_date: first, to_date: last } }),
        api.get("/attendance/summary", { params: { so_id: user.id, year, month } }),
      ]);
      setRows(list || []);
      setSummary(sum);
      const t = todayIST();
      setTodayRec((list || []).find((r) => r.date === t) || null);
    } catch (e) {
      // NOT a silent catch. The previous `catch (_) {}` left `rows` empty and
      // drew a full month of blank cells — a failed request rendered as "this
      // person checked in on no day this month", which is a claim about them.
      setRows([]);
      setSummary(null);
      setTodayRec(null);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (user?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, year, month]);

  function captureGps() {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) return resolve(null);
      const timer = setTimeout(() => resolve(null), 4000);
      navigator.geolocation.getCurrentPosition(
        (pos) => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        () => { clearTimeout(timer); resolve(null); },
        { timeout: 3500 }
      );
    });
  }

  async function doCheckIn() {
    setActing(true);
    try {
      const gps = await captureGps();
      const { data } = await api.post("/attendance/checkin", { gps_location: gps });
      setTodayRec(data);
      toast.success(data.status === "On Leave" ? "Marked on leave today" : "Checked in");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  async function doCheckOut() {
    setActing(true);
    try {
      const gps = await captureGps();
      const { data } = await api.put("/attendance/checkout", { gps_location: gps });
      setTodayRec(data);
      toast.success("Checked out");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  const byDate = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.date] = r;
    return m;
  }, [rows]);

  const { days } = useMemo(() => monthDaysIST(year, month), [year, month]);

  function shift(delta) {
    let nm = month + delta, ny = year;
    if (nm < 1) { nm = 12; ny -= 1; }
    else if (nm > 12) { nm = 1; ny += 1; }
    setYear(ny); setMonth(nm);
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-callisto rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="self-checkin-card">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">My attendance today</p>
          {todayRec ? (
            <div className="mt-1">
              <p className="text-lg font-bold text-callisto-navy flex items-center gap-2">
                Status: <StatusPill status={todayRec.status} testId="self-today-status" />
              </p>
              {todayRec.check_in_time && (
                <p className="text-xs text-callisto-muted mt-0.5 flex items-center gap-1.5">
                  <Clock size={12} /> in {formatISTTime(todayRec.check_in_time)}{" "}
                  {todayRec.check_out_time && <>· out {formatISTTime(todayRec.check_out_time)} · {todayRec.working_hours}h</>}
                </p>
              )}
              {todayRec.check_in_area && (
                <p className="text-xs text-callisto-muted mt-0.5 flex items-center gap-1.5">
                  <MapPin size={12} /> {todayRec.check_in_area}
                </p>
              )}
            </div>
          ) : error ? (
            <p className="mt-1 text-callisto-body" data-testid="self-today-unknown">
              Today's status could not be loaded.
            </p>
          ) : (
            <p className="mt-1 text-callisto-body" data-testid="self-today-none">
              No check-in captured for you today yet.
            </p>
          )}
        </div>
        <div>
          {error ? null : !todayRec ? (
            <Button
              onClick={doCheckIn}
              disabled={acting}
              className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base shadow-md"
              data-testid="self-checkin-button"
            >
              {acting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5 mr-2" /> Check In</>}
            </Button>
          ) : todayRec.status === "Present" && !todayRec.check_out_time ? (
            <Button
              onClick={doCheckOut}
              disabled={acting}
              className="h-12 px-6 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-bold text-base shadow-md"
              data-testid="self-checkout-button"
            >
              {acting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogOut className="h-5 w-5 mr-2" /> Check Out</>}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Own calendar/history (AT1 fix) */}
      <div className="bg-white border border-callisto rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-callisto-gold/10 text-callisto-gold px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]">
              <Sparkles size={12} />
              My history
            </span>
            <h2 className="mt-2 text-xl font-bold text-callisto-navy tracking-tight">
              {monthLabelIST(year, month)}
            </h2>
            <p className="mt-1">
              <AttendanceMonthSummary summary={summary} error={error} testId="self-attendance-summary" />
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => shift(-1)} className="h-9 w-9 p-0 border-callisto"><ChevronLeft size={16} /></Button>
            <Button variant="outline" onClick={() => shift(1)} className="h-9 w-9 p-0 border-callisto"><ChevronRight size={16} /></Button>
          </div>
        </div>

        <AttendanceCalendar
          days={days}
          byDate={byDate}
          loading={loading}
          error={error}
          testId="self-attendance-calendar"
        />
      </div>
    </div>
  );
}
